import React, { useEffect, useState } from 'react';
import { X, Printer, Camera } from 'lucide-react';
import VehicleDamageSketch from './VehicleDamageSketch';
import PhotoGallery from './PhotoGallery';
import { useOrganization } from '../../hooks/useOrganization';

const ViewJobCard = ({ jobCard, onClose }) => {
  const { organization } = useOrganization();
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);

  // Dynamic scaling - calculate based on total content to always fit one page
  const totalItems = (jobCard.services?.length || 0) + (jobCard.parts?.length || 0);
  const hasDamage = jobCard.damagePoints && jobCard.damagePoints.length > 0;
  const damagePoints = jobCard.damagePoints?.length || 0;
  
  // A4 page has ~1000px usable height at 96dpi with 3mm+6mm margins
  // Header ~60px, Info bar ~25px, Customer section ~60px, Signatures ~50px, Footer ~20px, Checklist ~250px, Damage ~200px
  // Fixed content ~665px at scale 1.0, leaving ~335px for tables
  // Each table row ~22px at scale 1.0
  const fixedHeight = 350 + (hasDamage ? 180 : 0) + 200; // header + info + customer + sigs + checklist + damage(if any)
  const tableRowHeight = 22;
  const availableForTables = 980 - fixedHeight; // A4 usable ~980px
  const neededForTables = totalItems * tableRowHeight + 60; // rows + 2 table headers
  
  const scale = neededForTables > availableForTables 
    ? Math.max(0.4, availableForTables / neededForTables)
    : Math.min(1.1, availableForTables / neededForTables);

  useEffect(() => {
    const s = scale;
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page { size: A4; margin: 3mm 8mm 6mm 8mm; }
        body * { visibility: hidden; }
        .print-content, .print-content * { visibility: visible; }
        .print-content { 
          position: absolute; left: 0; top: 0; width: 100%;
          font-size: ${10 * scale}px; line-height: 1.4; padding: 0;
          max-height: none !important; overflow: visible !important;
          border-radius: 0 !important;
          background: white !important;
        }
        .no-print { display: none !important; }
        .no-print-backdrop { 
          position: static !important; 
          background: none !important; 
          display: block !important;
          overflow: visible !important;
        }

        /* Header */
        .jc-header { display: flex !important; align-items: center; justify-content: space-between; padding: 6px 10px; background: #fff !important; color: #333 !important; border-bottom: 2px solid #333; margin-bottom: 0; }
        .jc-header img { height: ${45 * scale}px; width: ${45 * scale}px; object-fit: contain; border-radius: 6px; }
        .jc-header h1 { font-size: ${14 * scale}px; font-weight: bold; margin: 0; color: #333; }
        .jc-header p { font-size: ${8 * scale}px; margin: 0; color: #555; line-height: 1.4; }
        .jc-header .jc-title { font-size: ${16 * scale}px; font-weight: bold; color: #333; letter-spacing: 1px; text-transform: uppercase; }
        .jc-header .jc-number { font-size: ${11 * scale}px; color: #555; margin-top: 2px; }

        /* Order Info Bar */
        .jc-info-bar { display: flex !important; justify-content: space-between; padding: ${4 * scale}px 10px; background: #f5f5f5 !important; border-bottom: 1px solid #ddd; font-size: ${8 * scale}px; -webkit-print-color-adjust: exact; color-adjust: exact; }
        .jc-info-bar span { font-weight: 500; }
        .jc-info-bar strong { font-weight: 700; }

        /* Sections */
        .jc-body { display: block !important; padding: 4px 10px; }
        .jc-section { border: 1px solid #ccc; border-radius: 3px; margin-bottom: ${6 * scale}px; overflow: hidden; }
        .jc-section-head { background: #f9f9f9 !important; padding: ${3 * scale}px 8px; font-size: ${9 * scale}px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #ccc; color: #333; -webkit-print-color-adjust: exact; color-adjust: exact; }
        .jc-section-body { padding: ${4 * scale}px 8px; }
        .jc-row { display: flex; gap: 8px; margin-bottom: ${4 * scale}px; }
        .jc-col { flex: 1; }
        .jc-field { font-size: ${9 * scale}px; margin-bottom: 2px; }
        .jc-field strong { font-weight: 600; color: #333; }
        .jc-field span { color: #555; }

        /* Tables */
        .jc-table { width: 100%; border-collapse: collapse; }
        .jc-table th { background: #f0f0f0 !important; border: 1px solid #bbb; padding: ${2.5 * scale}px ${5 * scale}px; font-size: ${8 * scale}px; font-weight: bold; text-transform: uppercase; text-align: left; -webkit-print-color-adjust: exact; color-adjust: exact; }
        .jc-table td { border: 1px solid #ccc; padding: ${2.5 * scale}px ${5 * scale}px; font-size: ${9 * scale}px; }
        .jc-table .tc { text-align: center; }

        /* Damage */
        .jc-damage { display: flex; gap: 10px; align-items: flex-start; }
        .jc-damage img { width: 260px !important; height: auto !important; }
        .jc-damage .w-96 { width: 260px !important; }
        .jc-two-col { display: flex !important; gap: 10px; }
        .jc-two-col > div { flex: 1; }
        .damage-marker { position: absolute !important; width: 8px !important; height: 8px !important; border-radius: 50% !important; border: 1px solid #fff !important; transform: translate(-50%, -50%) !important; z-index: 10 !important; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
        .damage-marker-label { position: absolute !important; top: -14px !important; left: 50% !important; transform: translateX(-50%) !important; background: #000 !important; color: #fff !important; font-size: 6px !important; padding: 0 2px !important; border-radius: 1px !important; white-space: nowrap !important; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }

        /* Signature */
        .jc-signatures { display: flex !important; justify-content: space-between; margin-top: 25px; padding-top: 0; }
        .jc-sig-box { width: 28%; text-align: center; }
        .jc-sig-line { border-top: 1px solid #333; padding-top: 4px; font-size: 10px; font-weight: 500; }

        /* Footer - removed for job card */
        .jc-footer { display: none !important; }

        /* Checkbox row */
        .jc-checklist { display: flex; gap: 12px; flex-wrap: wrap; }
        .jc-check-item { display: flex; align-items: center; gap: 4px; font-size: 9px; }
        .jc-checkbox { width: 10px; height: 10px; border: 1px solid #333; display: inline-block; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [scale]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'WAITING_FOR_PARTS': return 'bg-orange-100 text-orange-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : '-';
  };

  const formatTime = (dateString) => {
    return dateString ? new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print-backdrop">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto print-content">
        
        {/* ===== PRINT LAYOUT ===== */}
        {/* Dark Header Band */}
        <div className="jc-header" style={{ display: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {organization?.logoUrl && (
              <img src={organization.logoUrl.startsWith('http') ? organization.logoUrl : `http://localhost:5000${organization.logoUrl}`} alt="Logo" />
            )}
            <div>
              <h1>{organization?.companyName || 'AGMS Auto Garage'}</h1>
              <p>{organization?.address}{organization?.city ? `, ${organization.city}` : ''}</p>
              <p>{organization?.companyContact} | {organization?.email}</p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="jc-title">JOB CARD</div>
            <div className="jc-number">#{jobCard.orderNo}</div>
          </div>
        </div>

        {/* Info Bar */}
        <div className="jc-info-bar" style={{ display: 'none' }}>
          <span><strong>Date:</strong> {formatDate(jobCard.createdAt)}</span>
          <span><strong>Branch:</strong> {jobCard.branch?.name || 'Main Branch'}</span>
          <span><strong>Technician:</strong> {jobCard.technician?.name || 'Unassigned'}</span>
          <span><strong>Status:</strong> {jobCard.status?.replace(/_/g, ' ')}</span>
          {jobCard.appointmentDate && <span><strong>Appt:</strong> {formatDate(jobCard.appointmentDate)} {formatTime(jobCard.appointmentDate)}</span>}
        </div>

        {/* Print Body */}
        <div className="hidden jc-body" style={{ display: 'none' }}>
          {/* Customer & Vehicle Section */}
          <div className="jc-section">
            <div className="jc-section-head">Customer & Vehicle Information</div>
            <div className="jc-section-body">
              <div className="jc-row">
                <div className="jc-col">
                  <div className="jc-field"><strong>Customer:</strong> <span>{jobCard.customer?.name || 'N/A'}</span></div>
                  <div className="jc-field"><strong>Contact:</strong> <span>{jobCard.customer?.contact || 'N/A'}</span></div>
                </div>
                <div className="jc-col">
                  <div className="jc-field"><strong>Vehicle:</strong> <span>{jobCard.vehicle?.make || ''} {jobCard.vehicle?.model || 'N/A'}</span></div>
                  <div className="jc-field"><strong>Plate No:</strong> <span>{jobCard.vehicleNumber || jobCard.vehicle?.plateNumber || 'N/A'}</span></div>
                </div>
                <div className="jc-col">
                  <div className="jc-field"><strong>VIN:</strong> <span>{jobCard.vinNumber || 'N/A'}</span></div>
                  <div className="jc-field"><strong>Odometer:</strong> <span>{jobCard.odometerReading || 'N/A'}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="jc-section">
            <div className="jc-section-head">Services</div>
            <div className="jc-section-body" style={{ padding: 0 }}>
              <table className="jc-table">
                <thead><tr><th className="tc" style={{width:'30px'}}>#</th><th>Description</th><th className="tc" style={{width:'50px'}}>Qty</th><th className="tc" style={{width:'70px'}}>Price</th></tr></thead>
                <tbody>
                  {jobCard.services && jobCard.services.length > 0 ? jobCard.services.map((s, i) => (
                    <tr key={i}><td className="tc">{i+1}</td><td>{s.name || s.description || s.service?.name || `Service #${i+1}`}</td><td className="tc">{s.quantity || 1}</td><td className="tc">{s.price || '-'}</td></tr>
                  )) : <tr><td colSpan="4" className="tc">No services</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Parts Section */}
          <div className="jc-section">
            <div className="jc-section-head">Parts Required</div>
            <div className="jc-section-body" style={{ padding: 0 }}>
              <table className="jc-table">
                <thead><tr><th className="tc" style={{width:'30px'}}>#</th><th>Part Name</th><th className="tc" style={{width:'50px'}}>Qty</th><th className="tc" style={{width:'70px'}}>Price</th></tr></thead>
                <tbody>
                  {jobCard.parts && jobCard.parts.length > 0 ? jobCard.parts.map((p, i) => (
                    <tr key={i}><td className="tc">{i+1}</td><td>{p.name || `Part #${i+1}`}</td><td className="tc">{p.quantity || 1}</td><td className="tc">{p.price || '-'}</td></tr>
                  )) : <tr><td colSpan="4" className="tc">No parts</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Damage Assessment & Checklist */}
          <div className={hasDamage ? "jc-two-col" : ""}>
            {/* Left: Vehicle Damage - only show if damage points exist */}
            {hasDamage && (
            <div className="jc-section">
              <div className="jc-section-head">Vehicle Damage Assessment</div>
              <div className="jc-section-body">
                <div>
                  <div className="print-sketch inline-block relative" style={{ marginBottom: '6px' }}>
                    <VehicleDamageSketch damagePoints={jobCard.damagePoints} readOnly={true} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '45%' }}>
                      {jobCard.damagePoints.slice(0, Math.ceil(jobCard.damagePoints.length / (jobCard.damagePoints.length > 6 ? 2 : 1))).map((point, index) => (
                        <div key={index} className="jc-field"><strong>P{index + 1}:</strong> <span>{point.label} ({point.damageType})</span></div>
                      ))}
                    </div>
                    {jobCard.damagePoints.length > 6 && (
                      <div style={{ flex: '1', minWidth: '45%' }}>
                        {jobCard.damagePoints.slice(Math.ceil(jobCard.damagePoints.length / 2)).map((point, index) => {
                          const actualIndex = Math.ceil(jobCard.damagePoints.length / 2) + index;
                          return (
                            <div key={actualIndex} className="jc-field"><strong>P{actualIndex + 1}:</strong> <span>{point.label} ({point.damageType})</span></div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Checklist - full width if no damage, half width if damage exists */}
            <div className="jc-section">
              <div className="jc-section-head">Pre-Service Checklist</div>
              <div className="jc-section-body" style={{ fontSize: `${Math.max(8, Math.round(11 * scale))}px`, lineHeight: `${Math.max(1.4, 2.0 * scale)}` }}>
                {/* Fluid Levels with gauge */}
                <div style={{ marginBottom: `${Math.round(8 * scale)}px` }}>
                  <div style={{ fontWeight: 'bold', fontSize: `${Math.max(8, Math.round(10 * scale))}px`, marginBottom: `${Math.round(4 * scale)}px`, borderBottom: '1px solid #ddd', paddingBottom: '2px' }}>FLUID LEVELS</div>
                  {['Fuel', 'Engine Oil', 'Coolant', 'Brake Fluid', 'Power Steering', 'Wiper Fluid', 'Transmission'].map((item, i) => {
                    const savedLevel = jobCard.checklist?.fluids?.[item];
                    return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: `${Math.round(5 * scale)}px`, marginBottom: `${Math.round(3 * scale)}px` }}>
                      <span style={{ width: `${Math.round(85 * scale)}px`, fontSize: `${Math.max(7, Math.round(10 * scale))}px` }}>{item}</span>
                      <span style={{ display: 'flex', gap: '2px' }}>
                        {['E', '¼', '½', '¾', 'F'].map((lvl, j) => (
                          <span key={j} style={{ width: `${Math.max(12, Math.round(16 * scale))}px`, height: `${Math.max(9, Math.round(12 * scale))}px`, border: '1px solid #666', display: 'inline-block', fontSize: `${Math.max(5, Math.round(7 * scale))}px`, textAlign: 'center', lineHeight: `${Math.max(9, Math.round(12 * scale))}px`, background: savedLevel === lvl ? (lvl === 'E' ? '#ef4444' : lvl === '¼' ? '#f97316' : lvl === '½' ? '#eab308' : lvl === '¾' ? '#86efac' : '#22c55e') : 'transparent', color: savedLevel === lvl ? '#fff' : '#333' }}>{lvl}</span>
                        ))}
                      </span>
                    </div>
                    );
                  })}
                </div>
                {/* Condition Checks */}
                <div style={{ marginBottom: `${Math.round(8 * scale)}px` }}>
                  <div style={{ fontWeight: 'bold', fontSize: `${Math.max(8, Math.round(10 * scale))}px`, marginBottom: `${Math.round(4 * scale)}px`, borderBottom: '1px solid #ddd', paddingBottom: '2px' }}>CONDITION (✓ = OK, ✗ = Issue)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${Math.round(3 * scale)}px` }}>
                    {['Battery', 'Tires (FL)', 'Tires (FR)', 'Tires (RL)', 'Tires (RR)', 'Spare Tire', 'Brakes', 'Wipers', 'Belts & Hoses', 'Exhaust', 'Suspension', 'Steering'].map((item, i) => {
                      const status = jobCard.checklist?.conditions?.[item];
                      return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: `${Math.max(8, Math.round(11 * scale))}px`, height: `${Math.max(8, Math.round(11 * scale))}px`, border: '1px solid #333', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: `${Math.max(6, Math.round(8 * scale))}px`, background: status === 'ok' ? '#22c55e' : status === 'issue' ? '#ef4444' : 'transparent', color: status ? '#fff' : '#333' }}>
                          {status === 'ok' ? '✓' : status === 'issue' ? '✗' : ''}
                        </span>
                        <span style={{ fontSize: `${Math.max(7, Math.round(10 * scale))}px` }}>{item}</span>
                      </div>
                      );
                    })}
                  </div>
                </div>
                {/* Functional Checks */}
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: `${Math.max(8, Math.round(10 * scale))}px`, marginBottom: `${Math.round(4 * scale)}px`, borderBottom: '1px solid #ddd', paddingBottom: '2px' }}>FUNCTIONAL & HANDOVER</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${Math.round(3 * scale)}px` }}>
                    {['Headlights', 'Tail Lights', 'Indicators', 'Horn', 'AC / Heater', 'Radio / Audio', 'Windows', 'Locks', 'Mirrors', 'Valuables Removed', 'Keys Received', 'Damage Noted', 'Photos Taken', 'Test Drive Done', 'Customer Informed'].map((item, i) => {
                      const status = jobCard.checklist?.functional?.[item];
                      return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ width: `${Math.max(8, Math.round(11 * scale))}px`, height: `${Math.max(8, Math.round(11 * scale))}px`, border: '1px solid #333', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: `${Math.max(6, Math.round(8 * scale))}px`, background: status === 'ok' ? '#22c55e' : status === 'issue' ? '#ef4444' : 'transparent', color: status ? '#fff' : '#333' }}>
                          {status === 'ok' ? '✓' : status === 'issue' ? '✗' : ''}
                        </span>
                        <span style={{ fontSize: `${Math.max(7, Math.round(10 * scale))}px` }}>{item}</span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Signatures */}
          <div className="jc-signatures">
            <div className="jc-sig-box"><div className="jc-sig-line">Service Advisor</div></div>
            <div className="jc-sig-box"><div className="jc-sig-line">Technician</div></div>
            <div className="jc-sig-box"><div className="jc-sig-line">Customer</div></div>
          </div>
        </div>

        {/* Footer */}
        <div className="jc-footer" style={{ display: 'none' }}>
          {organization?.companyName && <span>{organization.companyName}</span>}
          {organization?.companyContact && <span> | {organization.companyContact}</span>}
          {organization?.email && <span> | {organization.email}</span>}
          {organization?.address && <span> | {organization.address}</span>}
          {organization?.city && <span>, {organization.city}</span>}
        </div>

        {/* ===== SCREEN LAYOUT ===== */}
        <div className="flex justify-between items-center p-6 border-b no-print">
          <h2 className="text-xl font-semibold">Job Card - {jobCard.orderNo}</h2>
          <div className="flex space-x-2">
            <button onClick={() => setShowPhotoGallery(true)} className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
              <Camera className="w-4 h-4 mr-1" /> Photos
            </button>
            <button onClick={() => window.print()} className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-1" /> Print
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6 no-print">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-medium mb-1">Customer</h3>
                <div className="text-sm">
                  <div className="font-medium">{jobCard.customer?.name || 'N/A'}</div>
                  <div>{jobCard.customer?.contact || 'N/A'}</div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Vehicle</h3>
                <div className="text-sm space-y-1">
                  <div className="font-medium">{jobCard.vehicle?.make || ''} {jobCard.vehicle?.model || 'N/A'}</div>
                  {jobCard.vehicleNumber && <div>Plate: {jobCard.vehicleNumber}</div>}
                  {jobCard.vinNumber && <div>VIN: {jobCard.vinNumber}</div>}
                  {jobCard.odometerReading && <div>Odometer: {jobCard.odometerReading}</div>}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-medium mb-1">Status</h3>
                <span className={`px-2 py-1 text-xs rounded ${getStatusColor(jobCard.status)}`}>{jobCard.status?.replace(/_/g, ' ') || 'N/A'}</span>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Technician</h3>
                <div className="text-sm font-medium">{jobCard.technician?.name || 'Unassigned'}</div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">Appointment</h3>
                <div className="text-sm">{formatDate(jobCard.appointmentDate)} {formatTime(jobCard.appointmentDate)}</div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-medium mb-2">Services</h3>
            <div className="bg-gray-50 rounded p-3">
              {jobCard.services && jobCard.services.length > 0 ? jobCard.services.map((s, i) => (
                <div key={i} className="text-sm py-1 flex justify-between">
                  <span>{s.name || s.description || s.service?.name || `Service #${i+1}`} (x{s.quantity || 1})</span>
                  <span className="text-gray-600">{s.price || ''}</span>
                </div>
              )) : <div className="text-sm text-gray-500">No services</div>}
            </div>
          </div>

          {/* Parts */}
          <div>
            <h3 className="text-lg font-medium mb-2">Parts</h3>
            <div className="bg-gray-50 rounded p-3">
              {jobCard.parts && jobCard.parts.length > 0 ? jobCard.parts.map((p, i) => (
                <div key={i} className="text-sm py-1 flex justify-between">
                  <span>{p.name || `Part #${i+1}`} (x{p.quantity || 1})</span>
                  <span className="text-gray-600">{p.price || ''}</span>
                </div>
              )) : <div className="text-sm text-gray-500">No parts</div>}
            </div>
          </div>

          {/* Damage */}
          {jobCard.damagePoints && jobCard.damagePoints.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-2">Vehicle Damage</h3>
              <div className="flex gap-4">
                <div className="print-sketch inline-block relative">
                  <VehicleDamageSketch damagePoints={jobCard.damagePoints} readOnly={true} />
                </div>
                <div className="flex-1 space-y-1">
                  {jobCard.damagePoints.map((point, index) => (
                    <div key={index} className="text-xs"><span className="font-medium">P{index+1}:</span> {point.label} ({point.damageType})</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Comments */}
          {jobCard.comments && (
            <div>
              <h3 className="text-lg font-medium mb-2">History</h3>
              <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-3 rounded border">{jobCard.comments}</pre>
            </div>
          )}
        </div>
      </div>
      
      {showPhotoGallery && <PhotoGallery jobCardId={jobCard.id} onClose={() => setShowPhotoGallery(false)} />}
    </div>
  );
};

export default ViewJobCard;
